import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, input, output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { INotificationText, INotificationTextDrafts } from 'src/app/models';
import { NotificationTextLanguage, NotificationTypes } from 'src/app/models/enums';
import { NotificationSettingsService, UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';

@Component({
  selector: 'app-notification-texts-editor',
  templateUrl: './notification-texts-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass],
})
export class NotificationTextsEditorComponent {
  readonly clientId = input.required<number>();
  readonly event = input.required<string>();
  readonly texts = input.required<INotificationText[]>();

  readonly saved = output<INotificationText[]>();
  readonly closed = output<void>();

  readonly eventTexts = computed(() => this.texts().filter((text) => text.events.includes(this.event())));

  public readonly languages = Object.values(NotificationTextLanguage);
  public selectedLanguage: NotificationTextLanguage;
  public drafts: INotificationTextDrafts = {};
  public saving = false;

  constructor(
    public literalService: LiteralService,
    private notificationSettingsService: NotificationSettingsService,
    private utilsService: UtilsService,
    private translateService: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    const currentLanguage = this.translateService.getCurrentLang() as NotificationTextLanguage;
    this.selectedLanguage = this.languages.includes(currentLanguage) ? currentLanguage : NotificationTextLanguage.ES;
  }

  selectLanguage(language: string) {
    this.selectedLanguage = language as NotificationTextLanguage;
  }

  getValue(text: INotificationText, language = this.selectedLanguage) {
    return this.drafts[language]?.[text.key] ?? this.notificationSettingsService.getSentText(text, language);
  }

  setValue(text: INotificationText, value: string) {
    this.drafts = {
      ...this.drafts,
      [this.selectedLanguage]: { ...this.drafts[this.selectedLanguage], [text.key]: value },
    };
  }

  isDefault(text: INotificationText) {
    return this.getValue(text) === this.notificationSettingsService.getTranslation(text, this.selectedLanguage).defaultText;
  }

  restoreDefault(text: INotificationText) {
    this.setValue(text, this.notificationSettingsService.getTranslation(text, this.selectedLanguage).defaultText);
  }

  insertVariable(text: INotificationText, variable: string, textarea: HTMLTextAreaElement) {
    const value = this.getValue(text);
    const selectionStart = textarea.selectionStart ?? value.length;
    const selectionEnd = textarea.selectionEnd ?? selectionStart;
    const token = this.formatVariable(variable);
    this.setValue(text, `${value.slice(0, selectionStart)}${token}${value.slice(selectionEnd)}`);
    this.changeDetectorRef.detectChanges();
    const caretPosition = selectionStart + token.length;
    textarea.focus();
    textarea.setSelectionRange(caretPosition, caretPosition);
  }

  getUnknownVariables(text: INotificationText, language = this.selectedLanguage) {
    return this.notificationSettingsService.findUnknownVariables(text, this.getValue(text, language));
  }

  formatVariable(variable: string) {
    return `{${variable}}`;
  }

  formatVariables(variables: string[]) {
    return variables.map((variable) => this.formatVariable(variable)).join(', ');
  }

  getLanguagesWithUnknownVariables() {
    return this.languages.filter((language) =>
      this.eventTexts().some((text) => this.getUnknownVariables(text, language).length > 0),
    );
  }

  getLanguagesWithUnknownVariablesLabel() {
    return this.getLanguagesWithUnknownVariables()
      .map((language) => this.literalService.get(`languages.${language}`, true))
      .join(', ');
  }

  getUsagesLabel(text: INotificationText) {
    return text.usages.map((usage) => this.literalService.get(`notificationSettings.texts.usages.${usage}`)).join(' · ');
  }

  getSharedEventsLabel(text: INotificationText) {
    return text.events
      .filter((event) => event !== this.event())
      .map((event) => this.literalService.get(`notificationSettings.events.${event}`))
      .join(', ');
  }

  async save() {
    if (this.getLanguagesWithUnknownVariables().length > 0) return;
    const updates = this.notificationSettingsService.buildTextUpdates(this.eventTexts(), this.drafts);
    if (updates.length === 0) {
      this.closed.emit();
      return;
    }
    this.saving = true;
    try {
      const texts = await this.notificationSettingsService.updateClientTexts(this.clientId(), updates);
      this.drafts = {};
      this.saved.emit(texts);
      this.utilsService.showNotification(images.notifications.check, 'notificationSettings.texts.saved', NotificationTypes.SUCCESS);
    } catch {
      this.utilsService.showNotification(images.notifications.warning, 'notificationSettings.texts.error', NotificationTypes.DANGER);
    } finally {
      this.saving = false;
      this.changeDetectorRef.detectChanges();
    }
  }
}
